import { Field, Form, Formik, FormikErrors } from "formik"
import useTranslation from "next-translate/useTranslation"

import { IProject, IProjectCreation } from "@api/schema"
import { FormButtonArea } from "@components/common/form/FormButtonArea"
import FormikInput from "@components/common/form/FormikInput"
import GeneralFormError from "@components/common/form/GeneralFormError"
import TranslatedHtml from "@components/common/TranslatedHtml"
import { IFormikActions } from "@redux/helper/actions"

// validation of the form
// line break check must be as long as the backend does not allow linebreaks
const validate = (values: IProject): FormikErrors<IProject> => {
  const errors: { [key: string]: string } = {}

  if (!values.shortDescription) {
    errors.shortDescription = "validate.general.notBlank"
  } else if (values.shortDescription.trim().length < 10) {
    errors.shortDescription = "validate.general.tooShort"
  } else if (values.shortDescription.trim().length > 280) {
    errors.shortDescription = "validate.general.tooLong"
  } else if (/\r|\n/.exec(values.shortDescription)) {
    errors.shortDescription = "validate.general.containsLineBreaks"
  }

  return errors
}

interface IProps {
  onSubmit: (values: IProjectCreation, actions: IFormikActions) => void
  idea: IProject
}

/**
 * this component provides a "new idea" form
 */
const IdeaForm: React.FC<IProps> = (props: IProps) => {
  const { idea } = props
  const { t } = useTranslation("common")

  const onSubmit = (values: IProjectCreation, actions: IFormikActions): void => {
    // remove trailing spaces
    values.shortDescription = values.shortDescription.trim()
    props.onSubmit(values, actions)
  }

  return <Formik<IProject>
    initialValues={idea}
    onSubmit={onSubmit}
    validate={validate}
    validateOnChange={false}
  >
    {({
      errors,
      handleSubmit,
      isSubmitting,
      values,
    }) => <Form onSubmit={handleSubmit}>

        <h3><TranslatedHtml content={t("form.idea.header")} /></h3>

        <GeneralFormError
          errors={errors}
          prefix="project"
          excludeFields={["shortDescription"]}
        />

        <Field component={FormikInput}
          help="form.idea.shortDescription.help"
          label="form.idea.shortDescription.label"
          maxLength={280}
          name="shortDescription"
          placeholder="form.idea.shortDescription.placeholder"
          required
          type="textarea"
          value={values.shortDescription}
        />

        <p className="text-danger">{t("form.requiredFieldsHint")}</p>

        <FormButtonArea
          isSubmitting={isSubmitting}
          submitButton={{
            title: "form.idea.submit",
            iconName: "message"
          }}
        />
      </Form>
    }
  </Formik>
}

export default IdeaForm
